import { useEffect, useRef, useState } from 'react';
import {
  PALETTE, MIC_TYPE_LABEL, PRESENTER_ICONS,
  type MicType, type PresenterIcon,
} from '@micplot/shared';
import { api, type RosterMember } from '../lib/api';
import { downscaleToDataUrl } from '../lib/image';
import ConfirmDialog from './ConfirmDialog';
import Avatar, { ICON_GLYPH } from './Avatar';

interface Props {
  productionId: string;
  member: RosterMember;
  onClose: () => void;
  notify: (t: string, k?: 'info' | 'error') => void;
}

/**
 * Edit a member of the production's cast.
 *
 * Deliberately the same shape as the per-performance presenter editor: the same
 * person is being described, just at the durable level rather than for one
 * night.
 */
export default function CastEditor({ productionId, member, onClose, notify }: Props) {
  const [name, setName] = useState(member.name);
  const [characterName, setCharacterName] = useState(member.characterName ?? '');
  const [title, setTitle] = useState(member.title ?? '');
  const [color, setColor] = useState(member.color);
  const [mic, setMic] = useState<MicType>(member.micPreference);
  const [icon, setIcon] = useState<PresenterIcon | ''>(member.icon ?? '');
  const [shot, setShot] = useState(member.headshotUrl);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function upload(file: File) {
    setUploading(true);
    try {
      const dataUrl = await downscaleToDataUrl(file);
      const r = await api.setCastHeadshot(productionId, member.id, dataUrl);
      setShot(r.member.headshotUrl);
      notify('Photo updated.');
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    try {
      await api.updateCast(productionId, member.id, {
        name: name.trim() || member.name,
        characterName: characterName.trim(),
        title: title.trim(),
        color,
        micPreference: mic,
        icon: icon || '',
      });
      onClose();
    } catch (err) {
      notify((err as Error).message, 'error');
    }
  }

  const preview = { ...member, name, color, icon: icon || undefined, headshotUrl: shot };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-label="Edit cast member">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>Cast member</h2>
          <button className="btn sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="sub">Carried into every performance of this production.</p>

        <div className="row" style={{ gap: 14, marginBottom: 16 }}>
          <Avatar presenter={preview as any} size={64} />
          <div style={{ display: 'grid', gap: 6 }}>
            <div className="row">
              <button className="btn sm" onClick={() => cameraRef.current?.click()}>
                Take photo
              </button>
              <button className="btn sm" onClick={() => fileRef.current?.click()}>
                Choose file
              </button>
            </div>
            <div className="hint">{uploading ? 'Uploading…' : 'JPEG, PNG or WebP · up to 5MB'}</div>
          </div>
        </div>
        <input ref={cameraRef} type="file" hidden accept="image/*" capture="environment"
               onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        <input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp"
               onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />

        <div className="field">
          <label htmlFor="cname">Name</label>
          <input id="cname" value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cchar">Character / instrument</label>
          <input id="cchar" value={characterName} onChange={e => setCharacterName(e.target.value)}
                 placeholder="e.g. Ophelia, Lead Guitar" />
        </div>
        <div className="field">
          <label htmlFor="ctitle">Role / title</label>
          <input id="ctitle" value={title} onChange={e => setTitle(e.target.value)}
                 placeholder="e.g. Understudy, Musical Director" />
        </div>
        <div className="field">
          <label>Colour</label>
          <div className="swatches">
            {PALETTE.map((hex, i) => (
              <button key={hex} style={{ background: hex }} aria-pressed={color === i}
                      aria-label={`Colour ${i + 1}`} onClick={() => setColor(i)} />
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="cmic">Mic preference</label>
          <select id="cmic" value={mic} onChange={e => setMic(e.target.value as MicType)}>
            {(Object.keys(MIC_TYPE_LABEL) as MicType[]).map(t => (
              <option key={t} value={t}>{MIC_TYPE_LABEL[t]}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Icon (used when there is no photo)</label>
          <div className="icon-grid">
            <button className={`icon-btn${icon === '' ? ' on' : ''}`} onClick={() => setIcon('')}>—</button>
            {PRESENTER_ICONS.map(ic => (
              <button key={ic} className={`icon-btn${icon === ic ? ' on' : ''}`}
                      onClick={() => setIcon(ic)} title={ic}>{ICON_GLYPH[ic]}</button>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn danger" onClick={() => setConfirmDelete(true)}>Remove</button>
          <div className="header-spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={save}>Save</button>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title={`Remove ${member.name} from the cast?`}
          body="Performances already created keep their copy; only the production's cast changes."
          confirmLabel="Remove"
          danger
          onConfirm={async () => {
            setConfirmDelete(false);
            try { await api.removeCast(productionId, member.id); onClose(); }
            catch (err) { notify((err as Error).message, 'error'); }
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
